import { useEffect, useRef, useState } from 'react';
import { useReducedMotion } from 'framer-motion';

/** Cheapest reliable capability probe; the context is thrown away immediately. */
function hasWebGL() {
  try {
    const c = document.createElement('canvas');
    return !!(c.getContext('webgl2') || c.getContext('webgl'));
  } catch {
    return false;
  }
}

function readTheme() {
  const dark = document.documentElement.classList.contains('dark');
  const color =
    getComputedStyle(document.documentElement).getPropertyValue('--fg-primary').trim() ||
    (dark ? '#ededed' : '#171717');
  // Dark ink on a light page reads heavier than light ink on a dark one at the
  // same alpha, so the two are tuned apart to land at the same weight.
  return { color, opacity: dark ? 0.62 : 0.38 };
}

/**
 * The WebGL layer behind the hero.
 *
 * Everything in this file is a guard; the scene itself lives in
 * dotFieldScene.js.
 *
 *   - prefers-reduced-motion, no WebGL, or Save-Data: three is never fetched at
 *     all, and the parent's static CSS grid stands in.
 *   - off screen or tab hidden: the loop is stopped, not throttled.
 *   - coarse pointer: the cursor interaction is not wired up, because on a
 *     phone it can only fire from a tap and reads as a glitch.
 */
export default function HeroDotField({ contentRef, className = '' }) {
  const canvasRef = useRef(null);
  const [ready, setReady] = useState(false);
  const reduceMotion = useReducedMotion();

  useEffect(() => {
    if (reduceMotion) return undefined;
    if (!hasWebGL()) return undefined;
    if (navigator.connection?.saveData) return undefined; // this is decoration

    const canvas = canvasRef.current;
    if (!canvas) return undefined;

    let field = null;
    let disposed = false;
    const cleanups = [];

    (async () => {
      // Both dynamic, so three and the scene form their own chunk and never
      // block first paint.
      const [THREE, { createDotField }] = await Promise.all([
        import('three'),
        import('./dotFieldScene.js'),
      ]);
      if (disposed) return;

      const coarse = window.matchMedia('(hover: none)').matches;

      field = createDotField({
        THREE,
        canvas,
        getContentRect: () => contentRef.current?.getBoundingClientRect() ?? null,
        // A phone renders the same field at a third of the density. At this dot
        // size the difference is invisible; the battery cost is not.
        maxPoints: coarse ? 2200 : 6500,
        pointerEnabled: !coarse,
      });

      const theme = readTheme();
      field.setTheme(theme.color, theme.opacity);
      field.resize();
      field.renderOnce(); // paint before fading in, so there is no empty flash
      setReady(true);

      // The type is still mid-entrance when the first measurement happens, so
      // the clearing is sized against a box that is 16px out of place. Measure
      // again once the stagger has finished.
      const settle = setTimeout(() => !disposed && field.resize(), 1200);
      cleanups.push(() => clearTimeout(settle));

      // The dots are the same ink as the text, so they follow the theme toggle.
      const themeObserver = new MutationObserver(() => {
        const t = readTheme();
        field.setTheme(t.color, t.opacity);
      });
      themeObserver.observe(document.documentElement, {
        attributes: true,
        attributeFilter: ['class'],
      });
      cleanups.push(() => themeObserver.disconnect());

      const resizeObserver = new ResizeObserver(() => field.resize());
      resizeObserver.observe(canvas);
      cleanups.push(() => resizeObserver.disconnect());

      // Run only while actually on screen and the tab is in front.
      let onScreen = true;
      let visible = document.visibilityState === 'visible';
      const sync = () => (onScreen && visible ? field.start() : field.stop());

      const io = new IntersectionObserver(
        ([entry]) => {
          onScreen = entry.isIntersecting;
          sync();
        },
        { threshold: 0 }
      );
      io.observe(canvas);
      cleanups.push(() => io.disconnect());

      const onVisibility = () => {
        visible = document.visibilityState === 'visible';
        sync();
      };
      document.addEventListener('visibilitychange', onVisibility);
      cleanups.push(() => document.removeEventListener('visibilitychange', onVisibility));

      sync();
    })();

    return () => {
      disposed = true;
      cleanups.forEach((fn) => fn());
      field?.dispose();
    };
  }, [reduceMotion, contentRef]);

  return (
    <div aria-hidden="true" className={`pointer-events-none absolute inset-0 ${className}`}>
      {/* The base layer. It is what renders when any guard above declines to
          load three — reduced motion, no WebGL, Save-Data, or a failed import.
          Those readers get the composition, not a blank rectangle where the
          design was. It fades out only once the canvas has actually painted. */}
      <div
        className={`hero-dots-static absolute inset-0 transition-opacity duration-700 ${
          ready ? 'opacity-0' : 'opacity-100'
        }`}
      />

      {/* With motion reduced there is no canvas at all, not a paused one. */}
      {!reduceMotion && (
        <canvas
          ref={canvasRef}
          className={`absolute inset-0 h-full w-full transition-opacity duration-1000 ease-out ${
            ready ? 'opacity-100' : 'opacity-0'
          }`}
        />
      )}
    </div>
  );
}
